// NGRP trilingual dictionary — English / Hindi / Marathi
// Keep keys stable. Long-form manifesto/news/dashboard content is served from
// the backend (English) and does NOT need to be translated here for v1.

export const translations = {
  en: {
    common: {
      party_name_full: 'National Governance Reform Party',
      party_short: 'NGRP',
      motto: 'Citizen First • Nation First',
      tagline: 'Good Governance. Strong India. Brighter Future.',
      supporting_line: 'Transparent Governance. Merit-Based Leadership. A Stronger India.',
      join_cta: 'Join the Party',
      apply_cta: 'Apply to Become an NGRP Leader',
      read_more: 'Read more',
      view_details: 'View details',
      submit: 'Submit',
      submitting: 'Submitting…',
      loading: 'Loading…',
      language: 'Language',
      last_updated: 'Last updated',
      illustrative: 'Illustrative dataset',
      required: 'Required',
      thank_you: 'Thank you!',
      close: 'Close',
      back_home: 'Back to home',
    },
    nav: {
      home: 'Home',
      about: 'About',
      founder: 'Founder',
      vision: 'Founding Vision',
      emblem: 'Emblem',
      agenda: 'Agenda',
      dashboard: 'Dashboard',
      aspirant: 'Aspirant to Leader',
      manifesto: 'Manifesto',
      dialogue: 'Reform Dialogue',
      roadmap: 'Launch Blueprint',
      constitution: 'Constitution',
      national: 'Pan-India',
      join: 'Join',
      news: 'News',
      transparency: 'Transparency',
      contact: 'Contact',
      admin: 'Admin',
    },
    vision_page: {
      title: 'Founding Vision',
      lede: 'From Political Democracy to High-Performance Governance',
      why_reform: "Why India's Governance Needs Reform",
      transitions_title: 'NGRP believes government should move from',
      measurable_title: 'Government Should Have Measurable Performance',
      principles_title: 'The Ten NGRP Principles',
      call_title: 'Our Call',
      message_title: 'Our Message to India',
      promise_title: 'The NGRP Promise',
      what_ngrp: 'What NGRP is Really Trying to Build',
      what_ngrp_body: "NGRP should not first ask 'how do we capture power?' Our first question is: 'what institutions would make power accountable after we receive it?' Because changing one government does not automatically change governance. Systems create behaviour. If we want different outcomes, we must build better systems.",
    },
    hero: {
      eyebrow: 'National Governance Reform Party — NGRP',
      h1_line1: 'Time to Transform India.',
      h1_line2: 'From Red Tape to Red Carpet.',
      subhead: 'Transparent systems. Accountable institutions. Merit-based leadership. Citizen-first governance.',
      support: 'Building an accountable, technology-driven and citizen-first governance system for a stronger India.',
      cta_join: 'Join the Party',
      cta_agenda: 'Explore Our Agenda',
      pillars: {
        one: 'Zero Corruption',
        two: 'Merit-Based Leadership',
        three: 'Citizen First',
        four: 'Digital Governance',
        five: 'Future-Ready India',
      },
    },
    home: {
      dashboard_title: 'The Governance Reform Dashboard',
      dashboard_sub: 'A public, always-on view of how India is governed — because what gets measured, gets improved.',
      dashboard_cta: 'Open full dashboard',
      agenda_title: 'Our Agenda',
      agenda_sub: 'Eight priorities that define what an NGRP government would deliver.',
      news_title: 'Latest from NGRP',
      news_sub: 'Press releases, policy papers, meetings and campaign updates.',
      news_cta: 'See all updates',
      cta_band_title: 'Ready to help transform India?',
      cta_band_sub: 'Sign up as a member, volunteer or future candidate — or apply to the NGRP leadership pipeline.',
      cta_band_join: 'Join as a Member',
      cta_band_apply: 'Apply as an Aspirant',
    },
    about: {
      title: 'About NGRP',
      lede: 'A modern, citizen-first political movement built for the India of tomorrow.',
      vision_h: 'Our Vision',
      vision_p: 'An India where every citizen experiences transparent, accountable, technology-driven governance — and where merit, not privilege, defines public leadership.',
      mission_h: 'Our Mission',
      mission_p: 'Build a professional, ethical political party that delivers governance reform through digital public services, published performance data and time-bound service guarantees.',
      why_h: 'Why NGRP Was Founded',
      why_p: 'India is a young, ambitious republic held back by old bureaucracy and opaque political culture. NGRP was founded to close that gap — with reform, not rhetoric.',
      principles_h: 'Core Principles',
      principle_1: 'Integrity is non-negotiable.',
      principle_2: 'Accountability is a public commitment, not a private one.',
      principle_3: 'Merit is the only entry criterion for leadership.',
      principle_4: 'Technology serves citizens, not gatekeepers.',
      principle_5: 'The Constitution is the foundation of every decision.',
      leadership_h: 'Leadership Philosophy',
      leadership_p: 'Service over power. Responsibility over position. Professionals from every walk of life — UPSC/MPSC aspirants, engineers, doctors, lawyers, entrepreneurs and academics — leading a modern republic.',
      constitution_h: 'Constitutional Commitment',
      constitution_p: 'NGRP commits to constitutional democracy, the rule of law, protection of fundamental rights, and independent institutions — in letter and in spirit.',
      banner_caption: 'NGRP values banner (Marathi) — transparent governance, accountable institutions, empowered citizens.',
    },
    agenda: {
      title: 'Our Agenda',
      lede: 'Eight priorities. One republic. A time-bound plan for reform.',
      items: {
        zero_corruption: {
          title: 'Zero Corruption',
          desc: 'Digital workflows, transparent procurement and swift consequences — corruption designed out of the system, not around it.',
        },
        admin_reform: {
          title: 'Administrative Reform',
          desc: 'Time-bound approvals, single-window services and end-to-end file tracking. No more infinite waiting.',
        },
        merit_leadership: {
          title: 'Merit-Based Leadership',
          desc: 'Political leadership drawn from professionals, aspirants, entrepreneurs and policy experts — not dynasty and privilege.',
        },
        electoral_reform: {
          title: 'Electoral Reform',
          desc: 'Transparent funding, honest candidate declarations and independently audited electoral processes.',
        },
        youth_employment: {
          title: 'Youth & Employment',
          desc: 'Skills, entrepreneurship, apprenticeships and dignified formal jobs for every young Indian.',
        },
        infrastructure: {
          title: 'Infrastructure',
          desc: 'Faster execution, transparent contracts and 30-day statutory payment guarantees for public-works vendors.',
        },
        tech_gov: {
          title: 'Technology-Driven Government',
          desc: 'AI, digital public services, open data and independent cybersecurity — the operating system of a modern republic.',
        },
        citizen_first: {
          title: 'Citizen First',
          desc: 'Every service redesigned around the citizen, not the bureaucracy that runs it.',
        },
      },
    },
    dashboard: {
      title: 'Governance Reform Dashboard',
      lede: 'A public, always-on view of governance performance. Numbers below are illustrative and represent NGRP-proposed indicators for a nationwide accountability platform.',
      methodology_h: 'Methodology and Sources',
      methodology_p: 'Indicators are compiled from published departmental data and independent audits. NGRP proposes to institutionalise this dashboard as a statutory, tamper-proof public accountability platform, updated quarterly.',
      cadence: 'Cadence: quarterly public update',
      leaderboard_h: 'Department Performance Leaderboard',
      leaderboard_sub: 'Composite score based on speed, accuracy and citizen satisfaction (0–100).',
      col_rank: 'Rank',
      col_dept: 'Department',
      col_score: 'Score',
      col_trend: 'Trend',
      trust_h: 'Why NGRP publishes this',
      trust_p: 'We believe every citizen has a right to see how their government is performing — in near real time, in plain language, with no filter.',
    },
    aspirant: {
      title: 'Aspirant to Leader',
      lede: 'You Prepared to Serve India. Now Lead India.',
      description: 'NGRP invites civil-service aspirants, professionals and public-minded citizens to join a merit-based leadership pipeline for a modern republic.',
      who_h: 'Who should apply',
      list_1: 'UPSC and MPSC aspirants',
      list_2: 'Engineers and technologists',
      list_3: 'Doctors and healthcare professionals',
      list_4: 'Lawyers and legal researchers',
      list_5: 'Academics and researchers',
      list_6: 'Entrepreneurs and founders',
      list_7: 'Retired administrators and officers',
      list_8: 'Young professionals across sectors',
      form_h: 'Apply now',
      form_sub: 'Tell us about yourself. Our leadership team reviews every application.',
      success: 'Application received. Our leadership team will review it and reach out.',
    },
    manifesto: {
      title: 'Our Manifesto',
      lede: 'A 14-topic policy library covering the full arc of national reform — from governance to Vision 2047.',
      read_full: 'Read topic',
      principles_h: 'Guiding principles',
      actions_h: 'Key actions',
      kpis_h: 'Success measures',
      timeline_h: 'Timeline',
    },
    join: {
      title: 'Join the Party',
      lede: 'Add your voice to a citizen-first reform party for India. Membership is free and open to every eligible citizen.',
      form: {
        full_name: 'Full Name',
        mobile: 'Mobile Number',
        email: 'Email',
        state: 'State',
        district: 'District',
        profession: 'Profession',
        area_of_interest: 'Area of Interest',
        role: 'I would like to join as',
      },
      roles: {
        member: 'Member',
        volunteer: 'Volunteer',
        policy_expert: 'Policy Expert',
        future_candidate: 'Future Candidate',
        upsc_mpsc_aspirant: 'UPSC / MPSC Aspirant',
      },
      success: 'Welcome to NGRP! Your registration has been received.',
    },
    news: {
      title: 'News & Updates',
      lede: 'Press releases, policy papers, public meetings, speeches and campaign announcements.',
      filter_all: 'All categories',
    },
    transparency: {
      title: 'Transparency Portal',
      lede_line1: 'We Demand Transparency From Government.',
      lede_line2: 'We Practice It Ourselves.',
      description: 'NGRP publishes its donations, expenses, candidate declarations, internal governance rules, annual reports and conflict-of-interest disclosures — because reform begins at home.',
    },
    contact: {
      title: 'Contact NGRP',
      lede: 'Send us a message. Our team will get back to you.',
      form: {
        name: 'Full Name',
        email: 'Email',
        subject: 'Subject',
        message: 'Message',
      },
      success: 'Thank you for contacting NGRP. We will respond shortly.',
    },
    admin: {
      title: 'NGRP Admin',
      lede: 'Enter your admin token to view party form submissions.',
      token_label: 'Admin token',
      enter: 'Access dashboard',
      logout: 'Log out',
      tab_stats: 'Stats',
      tab_members: 'Members',
      tab_aspirants: 'Aspirants',
      tab_contact: 'Contact',
      total_members: 'Total members',
      total_aspirants: 'Total aspirants',
      total_messages: 'Total messages',
    },
    footer: {
      motto: 'Citizen First • Nation First',
      about_col: 'About',
      about_link: 'About NGRP',
      agenda_col: 'Agenda',
      resources_col: 'Resources',
      resources_manifesto: 'Manifesto',
      resources_dashboard: 'Governance Dashboard',
      resources_news: 'News',
      participate_col: 'Participate',
      participate_join: 'Join',
      participate_volunteer: 'Volunteer',
      participate_apply: 'Apply as Aspirant',
      participate_contact: 'Contact',
      accountability_col: 'Accountability',
      accountability_transparency: 'Transparency Portal',
      accountability_admin: 'Admin',
      copyright: '© 2026 National Governance Reform Party. All rights reserved.',
      privacy: 'Privacy',
      terms: 'Terms',
    },
  },
  hi: {
    common: {
      party_name_full: 'नेशनल गवर्नेंस रिफॉर्म पार्टी',
      party_short: 'एनजीआरपी',
      motto: 'नागरिक पहले • राष्ट्र पहले',
      tagline: 'सुशासन। सशक्त भारत। उज्ज्वल भविष्य।',
      supporting_line: 'पारदर्शी शासन। योग्यता-आधारित नेतृत्व। एक सशक्त भारत।',
      join_cta: 'पार्टी से जुड़ें',
      apply_cta: 'एनजीआरपी नेता बनने के लिए आवेदन करें',
      read_more: 'और पढ़ें',
      view_details: 'विवरण देखें',
      submit: 'जमा करें',
      submitting: 'भेजा जा रहा है…',
      loading: 'लोड हो रहा है…',
      language: 'भाषा',
      last_updated: 'अंतिम अपडेट',
      illustrative: 'दृष्टांतात्मक डेटा',
      required: 'आवश्यक',
      thank_you: 'धन्यवाद!',
      close: 'बंद करें',
      back_home: 'होम पर लौटें',
    },
    nav: {
      home: 'होम',
      about: 'हमारे बारे में',
      founder: 'संस्थापक',
      vision: 'संस्थापक दृष्टिकोण',
      emblem: 'प्रतीक चिह्न',
      agenda: 'एजेंडा',
      dashboard: 'डैशबोर्ड',
      aspirant: 'अभ्यर्थी से नेता',
      manifesto: 'घोषणापत्र',
      dialogue: 'सुधार संवाद',
      roadmap: 'शुभारंभ योजना',
      constitution: 'संविधान',
      national: 'अखिल भारतीय',
      join: 'जुड़ें',
      news: 'समाचार',
      transparency: 'पारदर्शिता',
      contact: 'संपर्क',
      admin: 'एडमिन',
    },
    vision_page: {
      title: 'संस्थापक दृष्टिकोण',
      lede: 'राजनीतिक लोकतंत्र से उच्च-प्रदर्शन शासन की ओर',
      why_reform: 'भारत के शासन में सुधार क्यों आवश्यक है',
      transitions_title: 'एनजीआरपी मानता है कि सरकार को यहाँ से यहाँ जाना चाहिए',
      measurable_title: 'सरकार का प्रदर्शन मापनीय होना चाहिए',
      principles_title: 'एनजीआरपी के दस सिद्धांत',
      call_title: 'हमारा आह्वान',
      message_title: 'भारत के नाम हमारा संदेश',
      promise_title: 'एनजीआरपी का वादा',
      what_ngrp: 'एनजीआरपी असल में क्या बनाना चाहता है',
      what_ngrp_body: 'एनजीआरपी को पहले यह नहीं पूछना चाहिए कि "हम सत्ता कैसे प्राप्त करें?" हमारा पहला प्रश्न है: "किन संस्थाओं से सत्ता प्राप्त करने के बाद उसे जवाबदेह बनाया जा सकता है?" क्योंकि एक सरकार बदलने से शासन स्वतः नहीं बदलता। व्यवस्थाएँ ही व्यवहार बनाती हैं।',
    },
    hero: {
      eyebrow: 'नेशनल गवर्नेंस रिफॉर्म पार्टी — एनजीआरपी',
      h1_line1: 'भारत को बदलने का समय।',
      h1_line2: 'लाल फीताशाही से लाल कालीन तक।',
      subhead: 'पारदर्शी व्यवस्था। जवाबदेह संस्थाएँ। योग्यता-आधारित नेतृत्व। नागरिक-प्रथम शासन।',
      support: 'एक जवाबदेह, तकनीक-आधारित और नागरिक-प्रथम शासन प्रणाली के निर्माण के लिए — एक सशक्त भारत के लिए।',
      cta_join: 'पार्टी से जुड़ें',
      cta_agenda: 'हमारा एजेंडा देखें',
      pillars: {
        one: 'शून्य भ्रष्टाचार',
        two: 'योग्यता-आधारित नेतृत्व',
        three: 'नागरिक प्रथम',
        four: 'डिजिटल शासन',
        five: 'भविष्य के लिए तैयार भारत',
      },
    },
    home: {
      dashboard_title: 'गवर्नेंस रिफॉर्म डैशबोर्ड',
      dashboard_sub: 'शासन कैसे चल रहा है — इसका सार्वजनिक, हमेशा उपलब्ध दृश्य। जो मापा जाता है, वही सुधरता है।',
      dashboard_cta: 'पूरा डैशबोर्ड खोलें',
      agenda_title: 'हमारा एजेंडा',
      agenda_sub: 'आठ प्राथमिकताएँ जो एनजीआरपी सरकार की परिभाषा तय करती हैं।',
      news_title: 'एनजीआरपी से नवीनतम',
      news_sub: 'प्रेस विज्ञप्तियाँ, नीति पत्र, जनसभाएँ और अभियान अपडेट।',
      news_cta: 'सभी अपडेट देखें',
      cta_band_title: 'भारत को बदलने में हमारी मदद करने के लिए तैयार हैं?',
      cta_band_sub: 'सदस्य, स्वयंसेवक या भावी उम्मीदवार के रूप में जुड़ें — या नेतृत्व पाइपलाइन के लिए आवेदन करें।',
      cta_band_join: 'सदस्य के रूप में जुड़ें',
      cta_band_apply: 'अभ्यर्थी के रूप में आवेदन करें',
    },
    about: {
      title: 'एनजीआरपी के बारे में',
      lede: 'कल के भारत के लिए बना एक आधुनिक, नागरिक-प्रथम राजनीतिक आंदोलन।',
      vision_h: 'हमारा दृष्टिकोण',
      vision_p: 'एक ऐसा भारत जहाँ हर नागरिक पारदर्शी, जवाबदेह और तकनीक-संचालित शासन का अनुभव करे — और जहाँ नेतृत्व की परिभाषा योग्यता तय करे, विशेषाधिकार नहीं।',
      mission_h: 'हमारा मिशन',
      mission_p: 'एक पेशेवर, नैतिक राजनीतिक दल का निर्माण जो डिजिटल सार्वजनिक सेवाओं, प्रकाशित प्रदर्शन आंकड़ों और समयबद्ध सेवा गारंटियों के माध्यम से शासन सुधार लाए।',
      why_h: 'एनजीआरपी की स्थापना क्यों हुई',
      why_p: 'भारत एक युवा, महत्वाकांक्षी गणराज्य है — पर पुरानी नौकरशाही और अपारदर्शी राजनीतिक संस्कृति उसे रोकती है। एनजीआरपी उस अंतर को पाटने के लिए स्थापित किया गया — बयानबाज़ी नहीं, सुधार से।',
      principles_h: 'मूल सिद्धांत',
      principle_1: 'ईमानदारी पर कोई समझौता नहीं।',
      principle_2: 'जवाबदेही एक सार्वजनिक प्रतिबद्धता है, निजी नहीं।',
      principle_3: 'नेतृत्व में प्रवेश का एकमात्र मापदंड योग्यता है।',
      principle_4: 'तकनीक नागरिकों की सेवा करती है, द्वारपालों की नहीं।',
      principle_5: 'हर निर्णय का आधार संविधान है।',
      leadership_h: 'नेतृत्व दर्शन',
      leadership_p: 'सत्ता से पहले सेवा। पद से पहले जिम्मेदारी। हर क्षेत्र के पेशेवर — यूपीएससी/एमपीएससी अभ्यर्थी, इंजीनियर, डॉक्टर, वकील, उद्यमी, शिक्षाविद — एक आधुनिक गणराज्य का नेतृत्व करते हुए।',
      constitution_h: 'संवैधानिक प्रतिबद्धता',
      constitution_p: 'एनजीआरपी संवैधानिक लोकतंत्र, विधि के शासन, मौलिक अधिकारों की रक्षा और स्वतंत्र संस्थाओं के प्रति — शब्द और भावना दोनों में — प्रतिबद्ध है।',
      banner_caption: 'एनजीआरपी मूल्य बैनर (मराठी) — पारदर्शी शासन, जवाबदेह संस्थाएँ, सशक्त नागरिक।',
    },
    agenda: {
      title: 'हमारा एजेंडा',
      lede: 'आठ प्राथमिकताएँ। एक गणराज्य। सुधार की समयबद्ध योजना।',
      items: {
        zero_corruption: { title: 'शून्य भ्रष्टाचार', desc: 'डिजिटल प्रक्रियाएँ, पारदर्शी खरीद और त्वरित परिणाम — भ्रष्टाचार को व्यवस्था में जगह ही नहीं।' },
        admin_reform: { title: 'प्रशासनिक सुधार', desc: 'समयबद्ध अनुमोदन, एकल-खिड़की सेवाएँ और पूर्ण फ़ाइल ट्रैकिंग। अनंत प्रतीक्षा का अंत।' },
        merit_leadership: { title: 'योग्यता-आधारित नेतृत्व', desc: 'राजनीतिक नेतृत्व पेशेवरों, अभ्यर्थियों, उद्यमियों और नीति विशेषज्ञों से — वंश और विशेषाधिकार से नहीं।' },
        electoral_reform: { title: 'चुनाव सुधार', desc: 'पारदर्शी धन, ईमानदार उम्मीदवार घोषणाएँ और स्वतंत्र रूप से लेखा-परीक्षित चुनाव प्रक्रियाएँ।' },
        youth_employment: { title: 'युवा और रोज़गार', desc: 'हर युवा भारतीय के लिए कौशल, उद्यमिता, प्रशिक्षुता और सम्मानजनक औपचारिक रोज़गार।' },
        infrastructure: { title: 'बुनियादी ढाँचा', desc: 'तेज़ निष्पादन, पारदर्शी अनुबंध और सार्वजनिक कार्यों के लिए 30-दिनी वैधानिक भुगतान गारंटी।' },
        tech_gov: { title: 'तकनीक-संचालित सरकार', desc: 'एआई, डिजिटल सार्वजनिक सेवाएँ, ओपन डेटा और स्वतंत्र साइबर सुरक्षा — आधुनिक गणराज्य का ऑपरेटिंग सिस्टम।' },
        citizen_first: { title: 'नागरिक प्रथम', desc: 'हर सेवा नागरिक के इर्द-गिर्द डिज़ाइन की गई — नौकरशाही के इर्द-गिर्द नहीं।' },
      },
    },
    dashboard: {
      title: 'गवर्नेंस रिफॉर्म डैशबोर्ड',
      lede: 'शासन प्रदर्शन का सार्वजनिक, हमेशा उपलब्ध दृश्य। नीचे दिए गए आँकड़े दृष्टांतात्मक हैं और एक राष्ट्रव्यापी जवाबदेही मंच के लिए एनजीआरपी-प्रस्तावित संकेतक हैं।',
      methodology_h: 'पद्धति और स्रोत',
      methodology_p: 'संकेतक प्रकाशित विभागीय डेटा और स्वतंत्र लेखा-परीक्षा से संकलित हैं। एनजीआरपी इस डैशबोर्ड को एक वैधानिक, छेड़छाड़-रोधी सार्वजनिक जवाबदेही मंच के रूप में संस्थागत बनाने का प्रस्ताव करता है।',
      cadence: 'आवृत्ति: तिमाही सार्वजनिक अपडेट',
      leaderboard_h: 'विभाग प्रदर्शन तालिका',
      leaderboard_sub: 'गति, सटीकता और नागरिक संतुष्टि पर आधारित संयुक्त स्कोर (0–100)।',
      col_rank: 'क्रम',
      col_dept: 'विभाग',
      col_score: 'स्कोर',
      col_trend: 'रुझान',
      trust_h: 'एनजीआरपी यह क्यों प्रकाशित करता है',
      trust_p: 'हम मानते हैं कि हर नागरिक को यह देखने का अधिकार है कि उसकी सरकार कैसा प्रदर्शन कर रही है — लगभग वास्तविक समय में, सरल भाषा में, बिना किसी फ़िल्टर के।',
    },
    aspirant: {
      title: 'अभ्यर्थी से नेता',
      lede: 'आपने भारत की सेवा करने की तैयारी की। अब भारत का नेतृत्व करें।',
      description: 'एनजीआरपी सिविल-सेवा अभ्यर्थियों, पेशेवरों और सार्वजनिक-चिंतित नागरिकों को एक योग्यता-आधारित नेतृत्व पाइपलाइन में शामिल होने का आमंत्रण देता है।',
      who_h: 'कौन आवेदन कर सकता है',
      list_1: 'यूपीएससी और एमपीएससी अभ्यर्थी',
      list_2: 'इंजीनियर और प्रौद्योगिकीविद',
      list_3: 'डॉक्टर और स्वास्थ्य पेशेवर',
      list_4: 'वकील और विधि शोधकर्ता',
      list_5: 'शिक्षाविद और शोधकर्ता',
      list_6: 'उद्यमी और संस्थापक',
      list_7: 'सेवानिवृत्त प्रशासक और अधिकारी',
      list_8: 'सभी क्षेत्रों के युवा पेशेवर',
      form_h: 'अभी आवेदन करें',
      form_sub: 'अपने बारे में बताएँ। हमारी नेतृत्व टीम हर आवेदन की समीक्षा करती है।',
      success: 'आवेदन प्राप्त हो गया। हमारी नेतृत्व टीम इसकी समीक्षा करेगी और आपसे संपर्क करेगी।',
    },
    manifesto: {
      title: 'हमारा घोषणापत्र',
      lede: '14 विषयों का नीति पुस्तकालय — शासन से लेकर विज़न 2047 तक राष्ट्रीय सुधार का पूर्ण चाप।',
      read_full: 'विषय पढ़ें',
      principles_h: 'मार्गदर्शक सिद्धांत',
      actions_h: 'मुख्य कार्रवाइयाँ',
      kpis_h: 'सफलता के मापदंड',
      timeline_h: 'समय-सीमा',
    },
    join: {
      title: 'पार्टी से जुड़ें',
      lede: 'भारत के लिए एक नागरिक-प्रथम सुधारवादी पार्टी में अपनी आवाज़ जोड़ें। सदस्यता निःशुल्क है और हर पात्र नागरिक के लिए खुली है।',
      form: {
        full_name: 'पूरा नाम',
        mobile: 'मोबाइल नंबर',
        email: 'ईमेल',
        state: 'राज्य',
        district: 'ज़िला',
        profession: 'व्यवसाय',
        area_of_interest: 'रुचि का क्षेत्र',
        role: 'मैं इस रूप में जुड़ना चाहता/चाहती हूँ',
      },
      roles: {
        member: 'सदस्य',
        volunteer: 'स्वयंसेवक',
        policy_expert: 'नीति विशेषज्ञ',
        future_candidate: 'भावी उम्मीदवार',
        upsc_mpsc_aspirant: 'यूपीएससी / एमपीएससी अभ्यर्थी',
      },
      success: 'एनजीआरपी में आपका स्वागत है! आपका पंजीकरण प्राप्त हो गया है।',
    },
    news: {
      title: 'समाचार और अपडेट',
      lede: 'प्रेस विज्ञप्तियाँ, नीति पत्र, जनसभाएँ, भाषण और अभियान घोषणाएँ।',
      filter_all: 'सभी श्रेणियाँ',
    },
    transparency: {
      title: 'पारदर्शिता पोर्टल',
      lede_line1: 'हम सरकार से पारदर्शिता की माँग करते हैं।',
      lede_line2: 'हम स्वयं इसका पालन करते हैं।',
      description: 'एनजीआरपी अपने दान, व्यय, उम्मीदवार घोषणाएँ, आंतरिक शासन नियम, वार्षिक रिपोर्टें और हितों के टकराव के प्रकटीकरण — सब कुछ सार्वजनिक रूप से प्रकाशित करता है।',
    },
    contact: {
      title: 'एनजीआरपी से संपर्क करें',
      lede: 'हमें संदेश भेजें। हमारी टीम आपसे संपर्क करेगी।',
      form: { name: 'पूरा नाम', email: 'ईमेल', subject: 'विषय', message: 'संदेश' },
      success: 'एनजीआरपी से संपर्क करने के लिए धन्यवाद। हम शीघ्र ही उत्तर देंगे।',
    },
    admin: {
      title: 'एनजीआरपी एडमिन',
      lede: 'पार्टी फ़ॉर्म सबमिशन देखने के लिए अपना एडमिन टोकन दर्ज करें।',
      token_label: 'एडमिन टोकन',
      enter: 'डैशबोर्ड खोलें',
      logout: 'लॉग आउट',
      tab_stats: 'आँकड़े',
      tab_members: 'सदस्य',
      tab_aspirants: 'अभ्यर्थी',
      tab_contact: 'संपर्क',
      total_members: 'कुल सदस्य',
      total_aspirants: 'कुल अभ्यर्थी',
      total_messages: 'कुल संदेश',
    },
    footer: {
      motto: 'नागरिक पहले • राष्ट्र पहले',
      about_col: 'हमारे बारे में',
      about_link: 'एनजीआरपी के बारे में',
      agenda_col: 'एजेंडा',
      resources_col: 'संसाधन',
      resources_manifesto: 'घोषणापत्र',
      resources_dashboard: 'शासन डैशबोर्ड',
      resources_news: 'समाचार',
      participate_col: 'भागीदारी',
      participate_join: 'जुड़ें',
      participate_volunteer: 'स्वयंसेवा',
      participate_apply: 'अभ्यर्थी बनें',
      participate_contact: 'संपर्क',
      accountability_col: 'जवाबदेही',
      accountability_transparency: 'पारदर्शिता पोर्टल',
      accountability_admin: 'एडमिन',
      copyright: '© 2026 नेशनल गवर्नेंस रिफॉर्म पार्टी। सर्वाधिकार सुरक्षित।',
      privacy: 'गोपनीयता',
      terms: 'नियम',
    },
  },
  mr: {
    common: {
      party_name_full: 'नॅशनल गव्हर्नन्स रिफॉर्म पार्टी',
      party_short: 'एनजीआरपी',
      motto: 'नागरिक प्रथम • राष्ट्र प्रथम',
      tagline: 'सुशासन. सशक्त भारत. उज्ज्वल भविष्य.',
      supporting_line: 'पारदर्शक शासन. गुणवत्तेवर आधारित नेतृत्व. एक सशक्त भारत.',
      join_cta: 'पक्षात सामील व्हा',
      apply_cta: 'एनजीआरपी नेते होण्यासाठी अर्ज करा',
      read_more: 'अधिक वाचा',
      view_details: 'तपशील पहा',
      submit: 'सबमिट करा',
      submitting: 'पाठवत आहे…',
      loading: 'लोड होत आहे…',
      language: 'भाषा',
      last_updated: 'शेवटचे अपडेट',
      illustrative: 'दृष्टांतात्मक डेटा',
      required: 'आवश्यक',
      thank_you: 'धन्यवाद!',
      close: 'बंद करा',
      back_home: 'होमपेजवर परत',
    },
    nav: {
      home: 'होम',
      about: 'आमच्याबद्दल',
      founder: 'संस्थापक',
      vision: 'संस्थापक दृष्टीकोन',
      emblem: 'बोधचिन्ह',
      agenda: 'अजेंडा',
      dashboard: 'डॅशबोर्ड',
      aspirant: 'उमेदवार ते नेता',
      manifesto: 'जाहीरनामा',
      dialogue: 'सुधार संवाद',
      roadmap: 'शुभारंभ आराखडा',
      constitution: 'घटना',
      national: 'अखिल भारतीय',
      join: 'सामील व्हा',
      news: 'बातम्या',
      transparency: 'पारदर्शकता',
      contact: 'संपर्क',
      admin: 'ॲडमिन',
    },
    vision_page: {
      title: 'संस्थापक दृष्टीकोन',
      lede: 'राजकीय लोकशाहीकडून उच्च-कामगिरी शासनाकडे',
      why_reform: 'भारताच्या शासनात सुधारणा का हवी',
      transitions_title: 'एनजीआरपीचा विश्वास आहे की सरकारने येथून येथे जावे',
      measurable_title: 'सरकारचे प्रदर्शन मोजण्यायोग्य असले पाहिजे',
      principles_title: 'एनजीआरपीची दहा तत्त्वे',
      call_title: 'आमचे आवाहन',
      message_title: 'भारताला आमचा संदेश',
      promise_title: 'एनजीआरपीचे वचन',
      what_ngrp: 'एनजीआरपी खरोखर काय उभे करू पाहत आहे',
      what_ngrp_body: 'एनजीआरपीने आधी हे विचारू नये की "आम्ही सत्ता कशी मिळवायची?" आमचा पहिला प्रश्न आहे: "सत्ता मिळाल्यानंतर तिला जबाबदार ठेवणाऱ्या कोणत्या संस्था असतील?" कारण एक सरकार बदलल्याने शासन आपोआप बदलत नाही. व्यवस्थाच वर्तन घडवतात.',
    },
    hero: {
      eyebrow: 'नॅशनल गव्हर्नन्स रिफॉर्म पार्टी — एनजीआरपी',
      h1_line1: 'भारत बदलण्याची वेळ.',
      h1_line2: 'लाल फितीपासून लाल गालिच्यापर्यंत.',
      subhead: 'पारदर्शक व्यवस्था. जबाबदार संस्था. गुणवत्तेवर आधारित नेतृत्व. नागरिक-केंद्रित शासन.',
      support: 'एक जबाबदार, तंत्रज्ञान-आधारित आणि नागरिक-केंद्रित शासन प्रणाली उभारण्यासाठी — एक सशक्त भारतासाठी.',
      cta_join: 'पक्षात सामील व्हा',
      cta_agenda: 'आमचा अजेंडा पहा',
      pillars: {
        one: 'शून्य भ्रष्टाचार',
        two: 'गुणवत्तेवर आधारित नेतृत्व',
        three: 'नागरिक प्रथम',
        four: 'डिजिटल शासन',
        five: 'भविष्यासाठी सज्ज भारत',
      },
    },
    home: {
      dashboard_title: 'गव्हर्नन्स रिफॉर्म डॅशबोर्ड',
      dashboard_sub: 'शासनाची सार्वजनिक, नेहमी उपलब्ध झलक. जे मोजले जाते, तेच सुधारते.',
      dashboard_cta: 'पूर्ण डॅशबोर्ड पहा',
      agenda_title: 'आमचा अजेंडा',
      agenda_sub: 'आठ प्राधान्ये जी एनजीआरपी सरकारला परिभाषित करतात.',
      news_title: 'एनजीआरपीकडून नवीनतम',
      news_sub: 'प्रेस प्रकाशने, धोरण पत्रे, जाहीर सभा आणि मोहीम अपडेट.',
      news_cta: 'सर्व अपडेट पहा',
      cta_band_title: 'भारत बदलण्यास मदत करण्यास तयार आहात?',
      cta_band_sub: 'सदस्य, स्वयंसेवक किंवा भावी उमेदवार म्हणून सामील व्हा — किंवा नेतृत्व पाइपलाइनसाठी अर्ज करा.',
      cta_band_join: 'सदस्य म्हणून सामील व्हा',
      cta_band_apply: 'उमेदवार म्हणून अर्ज करा',
    },
    about: {
      title: 'एनजीआरपीबद्दल',
      lede: 'उद्याच्या भारतासाठी बनवलेली आधुनिक, नागरिक-केंद्रित राजकीय चळवळ.',
      vision_h: 'आमचे दृष्टीकोन',
      vision_p: 'एक असा भारत जिथे प्रत्येक नागरिक पारदर्शक, जबाबदार आणि तंत्रज्ञान-आधारित शासनाचा अनुभव घेतो — आणि जिथे नेतृत्व गुणवत्तेने ठरते, विशेषाधिकाराने नाही.',
      mission_h: 'आमचे मिशन',
      mission_p: 'एक व्यावसायिक, नैतिक राजकीय पक्ष उभारणे जो डिजिटल सार्वजनिक सेवा, प्रकाशित कामगिरी डेटा आणि कालबद्ध सेवा हमीद्वारे शासन सुधारणा घडवून आणतो.',
      why_h: 'एनजीआरपीची स्थापना का',
      why_p: 'भारत एक तरुण, महत्त्वाकांक्षी प्रजासत्ताक आहे — परंतु जुनी नोकरशाही आणि अपारदर्शक राजकीय संस्कृती त्याला रोखते. एनजीआरपीची स्थापना तो अंतर भरण्यासाठी — भाषणे नव्हे, सुधारणांसाठी — केली गेली.',
      principles_h: 'मूलभूत तत्त्वे',
      principle_1: 'प्रामाणिकतेवर तडजोड नाही.',
      principle_2: 'जबाबदारी ही सार्वजनिक बांधिलकी आहे, खासगी नाही.',
      principle_3: 'नेतृत्वात प्रवेशाचा एकमेव निकष गुणवत्ता.',
      principle_4: 'तंत्रज्ञान नागरिकांची सेवा करते, द्वारपालांची नाही.',
      principle_5: 'प्रत्येक निर्णयाचा पाया संविधान आहे.',
      leadership_h: 'नेतृत्वाची तात्त्विक भूमिका',
      leadership_p: 'सत्तेपेक्षा सेवा. पदापेक्षा जबाबदारी. सर्व क्षेत्रांतील व्यावसायिक — यूपीएससी/एमपीएससी उमेदवार, अभियंते, डॉक्टर, वकील, उद्योजक, शिक्षणतज्ज्ञ — एक आधुनिक प्रजासत्ताकाचे नेतृत्व करत आहेत.',
      constitution_h: 'संवैधानिक बांधिलकी',
      constitution_p: 'एनजीआरपी संवैधानिक लोकशाही, कायद्याचे राज्य, मूलभूत हक्कांचे संरक्षण आणि स्वतंत्र संस्थांप्रती — शब्दात आणि भावनेत — कटिबद्ध आहे.',
      banner_caption: 'एनजीआरपी मूल्ये बॅनर — पारदर्शक शासन, जबाबदार संस्था, सशक्त नागरिक.',
    },
    agenda: {
      title: 'आमचा अजेंडा',
      lede: 'आठ प्राधान्ये. एक प्रजासत्ताक. सुधारणांसाठी कालबद्ध योजना.',
      items: {
        zero_corruption: { title: 'शून्य भ्रष्टाचार', desc: 'डिजिटल कार्यप्रवाह, पारदर्शक खरेदी आणि जलद परिणाम — भ्रष्टाचाराला व्यवस्थेत जागाच नाही.' },
        admin_reform: { title: 'प्रशासकीय सुधारणा', desc: 'कालबद्ध मंजुरी, एकल-खिडकी सेवा आणि संपूर्ण फाइल ट्रॅकिंग. अनंत प्रतीक्षेचा शेवट.' },
        merit_leadership: { title: 'गुणवत्तेवर आधारित नेतृत्व', desc: 'राजकीय नेतृत्व व्यावसायिक, उमेदवार, उद्योजक आणि धोरण तज्ज्ञांकडून — वंश आणि विशेषाधिकाराकडून नाही.' },
        electoral_reform: { title: 'निवडणूक सुधारणा', desc: 'पारदर्शक निधी, प्रामाणिक उमेदवार घोषणा आणि स्वतंत्रपणे लेखापरीक्षित निवडणूक प्रक्रिया.' },
        youth_employment: { title: 'युवा आणि रोजगार', desc: 'प्रत्येक तरुण भारतीयासाठी कौशल्ये, उद्योजकता, प्रशिक्षण आणि सन्माननीय औपचारिक रोजगार.' },
        infrastructure: { title: 'पायाभूत सुविधा', desc: 'जलद अंमलबजावणी, पारदर्शक करार आणि सार्वजनिक कामांसाठी 30-दिवसीय वैधानिक भरणा हमी.' },
        tech_gov: { title: 'तंत्रज्ञान-आधारित सरकार', desc: 'एआय, डिजिटल सार्वजनिक सेवा, ओपन डेटा आणि स्वतंत्र सायबर सुरक्षा — आधुनिक प्रजासत्ताकाची ऑपरेटिंग सिस्टीम.' },
        citizen_first: { title: 'नागरिक प्रथम', desc: 'प्रत्येक सेवा नागरिकाभोवती डिझाइन केलेली — नोकरशाहीभोवती नाही.' },
      },
    },
    dashboard: {
      title: 'गव्हर्नन्स रिफॉर्म डॅशबोर्ड',
      lede: 'शासन कामगिरीची सार्वजनिक, नेहमी उपलब्ध झलक. खालील आकडे दृष्टांतात्मक असून राष्ट्रव्यापी जबाबदारी व्यासपीठासाठी एनजीआरपी-प्रस्तावित निर्देशक आहेत.',
      methodology_h: 'पद्धत आणि स्रोत',
      methodology_p: 'निर्देशक प्रकाशित विभागीय डेटा आणि स्वतंत्र लेखापरीक्षणातून संकलित केले जातात. एनजीआरपी हा डॅशबोर्ड एक वैधानिक, छेडछाड-प्रतिबंधक सार्वजनिक जबाबदारी व्यासपीठ म्हणून संस्थात्मक करण्याचा प्रस्ताव देतो.',
      cadence: 'वारंवारता: तिमाही सार्वजनिक अपडेट',
      leaderboard_h: 'विभाग कामगिरी टेबल',
      leaderboard_sub: 'वेग, अचूकता आणि नागरिक समाधानावर आधारित एकत्रित गुण (0–100).',
      col_rank: 'क्रम',
      col_dept: 'विभाग',
      col_score: 'गुण',
      col_trend: 'कल',
      trust_h: 'एनजीआरपी हे का प्रकाशित करते',
      trust_p: 'आमचा विश्वास आहे की प्रत्येक नागरिकाला त्यांचे सरकार कसे काम करत आहे हे पाहण्याचा हक्क आहे — जवळजवळ रिअल-टाइममध्ये, सोप्या भाषेत, फिल्टरशिवाय.',
    },
    aspirant: {
      title: 'उमेदवार ते नेता',
      lede: 'तुम्ही भारताच्या सेवेची तयारी केली आहे. आता भारताचे नेतृत्व करा.',
      description: 'एनजीआरपी नागरी-सेवा उमेदवार, व्यावसायिक आणि सार्वजनिक-हिताच्या नागरिकांना गुणवत्तेवर आधारित नेतृत्व पाइपलाइनमध्ये सामील होण्याचे आमंत्रण देते.',
      who_h: 'कोण अर्ज करू शकतात',
      list_1: 'यूपीएससी आणि एमपीएससी उमेदवार',
      list_2: 'अभियंते आणि तंत्रज्ञ',
      list_3: 'डॉक्टर आणि आरोग्य व्यावसायिक',
      list_4: 'वकील आणि कायदा संशोधक',
      list_5: 'शिक्षणतज्ज्ञ आणि संशोधक',
      list_6: 'उद्योजक आणि संस्थापक',
      list_7: 'सेवानिवृत्त प्रशासक आणि अधिकारी',
      list_8: 'सर्व क्षेत्रांतील तरुण व्यावसायिक',
      form_h: 'आत्ता अर्ज करा',
      form_sub: 'स्वतःबद्दल सांगा. आमची नेतृत्व टीम प्रत्येक अर्जाचे पुनरावलोकन करते.',
      success: 'अर्ज प्राप्त झाला आहे. आमची नेतृत्व टीम त्याचे पुनरावलोकन करेल आणि संपर्क साधेल.',
    },
    manifesto: {
      title: 'आमचा जाहीरनामा',
      lede: '14 विषयांचे धोरण ग्रंथालय — शासनापासून व्हिजन 2047 पर्यंत राष्ट्रीय सुधारणांचा संपूर्ण आराखडा.',
      read_full: 'विषय वाचा',
      principles_h: 'मार्गदर्शक तत्त्वे',
      actions_h: 'महत्त्वाच्या कृती',
      kpis_h: 'यशाचे निकष',
      timeline_h: 'कालमर्यादा',
    },
    join: {
      title: 'पक्षात सामील व्हा',
      lede: 'भारतासाठी नागरिक-केंद्रित सुधारणा पक्षात तुमचा आवाज जोडा. सदस्यत्व विनामूल्य आहे आणि प्रत्येक पात्र नागरिकासाठी खुले आहे.',
      form: {
        full_name: 'पूर्ण नाव',
        mobile: 'मोबाइल नंबर',
        email: 'ईमेल',
        state: 'राज्य',
        district: 'जिल्हा',
        profession: 'व्यवसाय',
        area_of_interest: 'आवडीचे क्षेत्र',
        role: 'मी यात सामील होऊ इच्छितो/इच्छिते',
      },
      roles: {
        member: 'सदस्य',
        volunteer: 'स्वयंसेवक',
        policy_expert: 'धोरण तज्ज्ञ',
        future_candidate: 'भावी उमेदवार',
        upsc_mpsc_aspirant: 'यूपीएससी / एमपीएससी उमेदवार',
      },
      success: 'एनजीआरपीमध्ये आपले स्वागत आहे! तुमची नोंदणी प्राप्त झाली आहे.',
    },
    news: {
      title: 'बातम्या आणि अपडेट',
      lede: 'प्रेस प्रकाशने, धोरण पत्रे, जाहीर सभा, भाषणे आणि मोहीम घोषणा.',
      filter_all: 'सर्व श्रेणी',
    },
    transparency: {
      title: 'पारदर्शकता पोर्टल',
      lede_line1: 'आम्ही सरकारकडून पारदर्शकतेची मागणी करतो.',
      lede_line2: 'आम्ही स्वतः त्याचे पालन करतो.',
      description: 'एनजीआरपी आपले देणगी, खर्च, उमेदवार घोषणा, अंतर्गत शासन नियम, वार्षिक अहवाल आणि हितसंबंध संघर्ष प्रकटीकरण — सर्व काही सार्वजनिकरित्या प्रकाशित करते.',
    },
    contact: {
      title: 'एनजीआरपीशी संपर्क साधा',
      lede: 'आम्हाला संदेश पाठवा. आमची टीम तुमच्याशी संपर्क साधेल.',
      form: { name: 'पूर्ण नाव', email: 'ईमेल', subject: 'विषय', message: 'संदेश' },
      success: 'एनजीआरपीशी संपर्क साधल्याबद्दल धन्यवाद. आम्ही लवकरच प्रतिसाद देऊ.',
    },
    admin: {
      title: 'एनजीआरपी ॲडमिन',
      lede: 'पक्षाच्या फॉर्म सबमिशन पाहण्यासाठी तुमचा ॲडमिन टोकन प्रविष्ट करा.',
      token_label: 'ॲडमिन टोकन',
      enter: 'डॅशबोर्ड उघडा',
      logout: 'लॉग आउट',
      tab_stats: 'आकडेवारी',
      tab_members: 'सदस्य',
      tab_aspirants: 'उमेदवार',
      tab_contact: 'संपर्क',
      total_members: 'एकूण सदस्य',
      total_aspirants: 'एकूण उमेदवार',
      total_messages: 'एकूण संदेश',
    },
    footer: {
      motto: 'नागरिक प्रथम • राष्ट्र प्रथम',
      about_col: 'आमच्याबद्दल',
      about_link: 'एनजीआरपीबद्दल',
      agenda_col: 'अजेंडा',
      resources_col: 'संसाधने',
      resources_manifesto: 'जाहीरनामा',
      resources_dashboard: 'शासन डॅशबोर्ड',
      resources_news: 'बातम्या',
      participate_col: 'सहभाग',
      participate_join: 'सामील व्हा',
      participate_volunteer: 'स्वयंसेवा',
      participate_apply: 'उमेदवार बना',
      participate_contact: 'संपर्क',
      accountability_col: 'जबाबदारी',
      accountability_transparency: 'पारदर्शकता पोर्टल',
      accountability_admin: 'ॲडमिन',
      copyright: '© 2026 नॅशनल गव्हर्नन्स रिफॉर्म पार्टी. सर्व हक्क राखीव.',
      privacy: 'गोपनीयता',
      terms: 'अटी',
    },
  },
};
